import { Box, Button, Center, Container, Flex, Grid, GridItem, Heading, Image, Skeleton, Spinner, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { HiArrowLeft } from 'react-icons/hi2';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import getStaticAsset from '../../helper/utils/utilsStaticAsset';
import { doHandleState } from '../../redux/onlineSales/onlineSales.actions';
import useOnlineSales from '../onlineSales/hooks/useOnlineSales';
import usePages from '../onlineSales/hooks/usePages';

export default function PageLayoutLocation() {
    const dispatch = useDispatch();
    const { handleGetBrand, pages, isLoading } = usePages();
    const { outlets: outletData } = useOnlineSales();

    const params = useParams();
    const navigate = useNavigate();

    const outletId = (params['outletId'] || '').toString().trim().toLowerCase();
    const brandId = (params['brandId'] || '').toString().trim().toLowerCase();

    const [isAutoSelecting, setIsAutoSelecting] = useState(brandId !== '' && outletId !== '');

    const handleSelectOutlet = (page, selectedOutlet, onSuccess) => {
        localStorage.setItem('outlet', JSON.stringify(selectedOutlet));
        localStorage.setItem('page', JSON.stringify(page));

        dispatch(doHandleState('page', page));
        dispatch(doHandleState('currentTheme', page['theme'] || {}));

        if (typeof onSuccess === 'function') {
            onSuccess();
        }
    };

    const handleFilteredOutlets = (brandId) => {
        if (brandId && brandId != '') {
            setIsAutoSelecting(true);
            const filteredPages =
                pages?.find((page) => {
                    const pageId = (page['slug'] || '').toString().trim().toLowerCase();
                    const brandCode = (brandId || '').toString().trim().toLowerCase();

                    return pageId === brandCode;
                }) || {};

            if (Object.keys(filteredPages).length > 0) {
                const brand = filteredPages['id'];
                handleGetBrand(brand, (response) => {
                    handleAutoSelectOutlet(response);
                    setIsAutoSelecting(false);
                });

                return;
            }

            setIsAutoSelecting(false);
            return;
        }
    };

    const handleGetPageData = (outlet = {}) => {
        const filteredPage = pages?.filter((pg) => pg['id'] === outlet['brandCode']);
        const pageData = filteredPage.length > 0 ? { ...filteredPage[0], ...outlet } : {};

        return pageData;
    };

    const handleAutoSelectOutlet = (brand = {}) => {
        if (Object.keys(brand).length > 0 && outletId != '') {
            const outlets = brand['outlets'] || [];
            const filteredOutlets = outlets?.filter((outlet) => (outlet['outletName'] || '').toString().trim().toLowerCase() === outletId);
            const outlet = filteredOutlets.length > 0 ? filteredOutlets[0] : {};

            if (Object.keys(outlet).length > 0) {
                const page = handleGetPageData(outlet);
                handleSelectOutlet(page, outlet, () => {});
                return;
            }

            return;
        }
    };

    useEffect(() => {
        if (outletId == '') {
            localStorage.setItem('page', JSON.stringify({}));
        }
    }, [outletId]);

    useEffect(() => {
        if (brandId && brandId !== '') {
            handleFilteredOutlets(brandId);
        }
    }, [brandId, outletId]);

    if (isLoading || isAutoSelecting) {
        if (outletId != '') {
            return (
                <Center h='100vh' w='full'>
                    <Spinner size='xl' color='blue.500' thickness='4px' />
                </Center>
            );
        }
    }

    if (outletId != '') {
        return <Outlet />;
    }

    return (
        <Box minH='100vh' bg={'#f7fafc'} py={16}>
            <Container maxW='container.lg'>
                <Flex direction='column' align='center' mb={12}>
                    <Heading as='h1' size='2xl' textAlign='center' mb={4} fontWeight='extrabold'>
                        Where do you want to play?
                    </Heading>
                    <Text fontSize='lg' color='gray.600' textAlign='center'>
                        Choose one of our locations below to start your adventure.
                    </Text>
                </Flex>

                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={8}>
                    {isLoading
                        ? [1, 2].map((i) => (
                              <GridItem key={i}>
                                  <Skeleton height='300px' rounded='3xl' />
                              </GridItem>
                          ))
                        : outletData?.map((item, index) => {
                              const pageData = handleGetPageData(item);
                              const color = pageData['theme']?.['colors']?.['primary']?.['500'] || 'blue.500';
                              const lightColor = pageData['theme']?.['colors']?.['primary']?.['100'] || 'blue.50';

                              return (
                                  <GridItem key={index}>
                                      <Box
                                          bg='white'
                                          p={8}
                                          rounded='3xl'
                                          shadow='lg'
                                          transition='all 0.3s ease'
                                          _hover={{
                                              transform: 'translateY(-8px)',
                                              shadow: '2xl',
                                              borderColor: color,
                                              borderWidth: '2px',
                                          }}
                                          borderWidth='2px'
                                          borderColor='transparent'
                                          cursor='pointer'
                                          onClick={() =>
                                              handleSelectOutlet(pageData, item, () => {
                                                  dispatch(doHandleState('cart', []));

                                                  const outletCode = (item['outletName'] || '').toString().trim().toLowerCase();
                                                  navigate(`/${brandId}/${outletCode}/online-sales/home`);
                                              })
                                          }
                                          h='full'
                                          display='flex'
                                          flexDirection='column'
                                          alignItems='center'
                                          textAlign='center'
                                          position='relative'
                                          overflow='hidden'>
                                          <Box position='absolute' top='-10%' right='-5%' w='120px' h='120px' bg={lightColor} rounded='full' opacity='0.4' />

                                          <Image src={getStaticAsset(pageData['logo'])} alt={`${pageData['title']} Logo`} maxH='80px' mb={6} objectFit='contain' zIndex={1} />
                                          <Heading size='md' mb={2} zIndex={1}>
                                              {item['desc'] || item['outletName'] || ''}
                                          </Heading>
                                          <Text fontSize='sm' color='gray.500' noOfLines={3} zIndex={1}>
                                              {item['address'] || ''}
                                          </Text>
                                      </Box>
                                  </GridItem>
                              );
                          })}
                </Grid>

                <Flex justify='center' mt={16}>
                    <Button leftIcon={<HiArrowLeft />} variant='ghost' colorScheme='gray' size='lg' onClick={() => navigate('/')} _hover={{ bg: 'white', transform: 'translateX(-5px)' }} transition='all 0.2s'>
                        Back to Brand Selection
                    </Button>
                </Flex>
            </Container>
        </Box>
    );
}
